import { useRouterState } from "@tanstack/react-router";

export type Locale = "sv" | "en" | "es";

export type PageEntry = {
  /** Svensk sökväg (utan /en-prefix) */
  sv: string;
  /** Engelsk sökväg (inklusive /en-prefix) */
  en: string;
  labelSv: string;
  labelEn: string;
  group?: "school" | "sport" | "practical" | "guides" | "legal" | "none";
  /** Lyft fram posten visuellt i menyn */
  emphasize?: boolean;
};

export const PAGES: PageEntry[] = [
  { sv: "/", en: "/en", labelSv: "Start", labelEn: "Home", group: "none" },

  { sv: "/om-skolan", en: "/en/about-the-school", labelSv: "Om skolan", labelEn: "About the school", group: "school" },
  { sv: "/forskola", en: "/en/preschool", labelSv: "Förskola", labelEn: "Preschool", group: "school" },
  {
    sv: "/forskoleklass",
    en: "/en/preschool-class",
    labelSv: "Förskoleklass",
    labelEn: "Preschool class",
    group: "school",
  },
  { sv: "/grundskola", en: "/en/primary-school", labelSv: "Grundskola", labelEn: "Primary school", group: "school" },
  {
    sv: "/hogstadium-gymnasium",
    en: "/en/secondary-school-sixth-form",
    labelSv: "Högstadium & gymnasium",
    labelEn: "Secondary school & sixth form",
    group: "school",
  },
  {
    sv: "/fritidsverksamhet",
    en: "/en/leisure-activities",
    labelSv: "Fritidsverksamhet",
    labelEn: "Leisure activities",
    group: "school",
  },
  { sv: "/personal", en: "/en/personal", labelSv: "Personal", labelEn: "Our staff", group: "school" },
  { sv: "/omdomen", en: "/en/reviews", labelSv: "Omdömen", labelEn: "Reviews", group: "school" },
  {
    sv: "/elevhalsoteamet",
    en: "/en/student-health-team",
    labelSv: "Elevhälsoteamet",
    labelEn: "Student health team",
    group: "school",
  },
  {
    sv: "/extra-stod",
    en: "/en/extra-support",
    labelSv: "Elever i behov av extra stöd",
    labelEn: "Pupils needing extra support",
    group: "school",
  },
  { sv: "/arskurser", en: "/en/grade-levels", labelSv: "Årskurser", labelEn: "Grade levels", group: "school" },

  {
    sv: "/fotbollsakademi",
    en: "/en/football-academy",
    labelSv: "Malmö FF:s fotbollsakademi",
    labelEn: "Malmö FF football academy",
    group: "sport",
  },
  { sv: "/padel-tennis", en: "/en/padel-tennis", labelSv: "Padel & tennis", labelEn: "Padel & tennis", group: "sport" },
  {
    sv: "/hostlovscamp-mff-2026",
    en: "/en/football-camp-mff-2026",
    labelSv: "Fotbollscamp",
    labelEn: "Football camp",
    group: "sport",
  },
  { sv: "/spanskakurs", en: "/en/learn-spanish", labelSv: "Lär dig spanska", labelEn: "Learn Spanish", group: "sport" },

  { sv: "/kontakt", en: "/en/contact-us", labelSv: "Kontakt", labelEn: "Contact us", group: "practical", emphasize: true },
  {
    sv: "/boka-besok",
    en: "/en/book-a-visit",
    labelSv: "Boka ett besök",
    labelEn: "Book a visit",
    group: "practical",
    emphasize: true,
  },
  { sv: "/avgifter", en: "/en/fees", labelSv: "Avgifter", labelEn: "Fees", group: "practical" },
  {
    sv: "/lasarstider",
    en: "/en/school-year-dates",
    labelSv: "Läsårstider",
    labelEn: "School year dates",
    group: "practical",
  },
  { sv: "/matsedel", en: "/en/menu", labelSv: "Matsedel", labelEn: "Menu", group: "practical" },
  {
    sv: "/fragor-och-svar",
    en: "/en/questions-and-answers",
    labelSv: "Frågor och svar",
    labelEn: "Questions and answers",
    group: "practical",
  },
  { sv: "/blogg", en: "/en/blog", labelSv: "Blogg", labelEn: "Blog", group: "practical" },
  { sv: "/instagram", en: "/en/instagram", labelSv: "Instagram", labelEn: "Instagram", group: "practical" },
  {
    sv: "/prenumerera",
    en: "/en/subscribe",
    labelSv: "Prenumerera",
    labelEn: "Subscribe",
    group: "practical",
  },
  {
    sv: "/flyttgenomgang",
    en: "/en/relocation-consultation",
    labelSv: "Genomgång inför flytten",
    labelEn: "Relocation consultation",
    group: "practical",
  },
  {
    sv: "/lediga-tjanster",
    en: "/en/vacancies",
    labelSv: "Lediga tjänster",
    labelEn: "Vacancies",
    group: "practical",
  },
  { sv: "/sponsorer", en: "/en/sponsors", labelSv: "Sponsorer", labelEn: "Sponsors", group: "practical" },
  {
    sv: "/svenska-skolverket-och-kompletterande-svensk-undervisning",
    en: "/en/swedish-national-agency-for-education",
    labelSv: "Skolverket & kompletterande svenska",
    labelEn: "Swedish National Agency for Education",
    group: "practical",
  },
  {
    sv: "/junta-de-andalucia-och-utbildning",
    en: "/en/junta-de-andalucia",
    labelSv: "Junta de Andalucía",
    labelEn: "Junta de Andalucía",
    group: "practical",
  },

  { sv: "/ansokan", en: "/en/application", labelSv: "Ansökan", labelEn: "Application", group: "none" },

  { sv: "/gemenskap", en: "/en/community", labelSv: "Gemenskap", labelEn: "Community", group: "guides" },
  {
    sv: "/flytta-till-spanien-med-barn",
    en: "/en/moving-to-spain-with-children",
    labelSv: "Flytta till Spanien med barn",
    labelEn: "Moving to Spain with children",
    group: "guides",
  },
  {
    sv: "/svensk-skola-i-spanien",
    en: "/en/swedish-school-in-spain",
    labelSv: "Svensk skola i Spanien",
    labelEn: "Swedish school in Spain",
    group: "guides",
  },
  {
    sv: "/skola-i-marbella-och-estepona",
    en: "/en/schools-in-marbella-and-estepona",
    labelSv: "Skola i Marbella och Estepona",
    labelEn: "Schools in Marbella and Estepona",
    group: "guides",
  },
  {
    sv: "/svensk-skola-marbella",
    en: "/en/swedish-school-marbella",
    labelSv: "Svensk skola nära Marbella",
    labelEn: "Swedish school near Marbella",
    group: "none",
  },
  {
    sv: "/skola-estepona",
    en: "/en/school-estepona",
    labelSv: "Skola i Estepona",
    labelEn: "School in Estepona",
    group: "none",
  },
  {
    sv: "/skola-cancelada",
    en: "/en/school-cancelada",
    labelSv: "Skola i Cancelada",
    labelEn: "School in Cancelada",
    group: "none",
  },
  {
    sv: "/svensk-internationell-eller-spansk-skola",
    en: "/en/swedish-international-or-spanish-school",
    labelSv: "Svensk, internationell eller spansk skola?",
    labelEn: "Swedish, international or Spanish school?",
    group: "none",
  },
  {
    sv: "/nie-och-folkbokforing-i-spanien",
    en: "/en/nie-and-registration-in-spain",
    labelSv: "NIE och folkbokföring",
    labelEn: "NIE and registration in Spain",
    group: "guides",
  },
  {
    sv: "/distansstudier-hogstadiet-gymnasiet-utomlands",
    en: "/en/distance-learning-abroad",
    labelSv: "Distansstudier utomlands",
    labelEn: "Distance learning abroad",
    group: "guides",
  },
  {
    sv: "/vad-kostar-svensk-skola-i-spanien",
    en: "/en/what-does-swedish-school-cost-in-spain",
    labelSv: "Vad kostar svensk skola i Spanien?",
    labelEn: "What does Swedish school cost in Spain?",
    group: "guides",
  },

  {
    sv: "/guider",
    en: "/en/guides",
    labelSv: "Alla guider",
    labelEn: "All guides",
    group: "guides",
  },
  {
    sv: "/kunskapsbank",
    en: "/en/knowledge-hub",
    labelSv: "Kunskapsbank",
    labelEn: "Knowledge hub",
    group: "guides",
  },
  {
    sv: "/kunskapsbank/flyttchecklistan",
    en: "/en/knowledge-hub/relocation-checklist",
    labelSv: "Flyttchecklistan",
    labelEn: "Relocation checklist",
    group: "none",
  },
  {
    sv: "/kunskapsbank/myndigheter-och-papper",
    en: "/en/knowledge-hub/paperwork-and-authorities",
    labelSv: "Myndigheter och papper",
    labelEn: "Paperwork and authorities",
    group: "none",
  },
  {
    sv: "/kunskapsbank/vardagen-i-spanien",
    en: "/en/knowledge-hub/everyday-life-in-spain",
    labelSv: "Vardagen i Spanien",
    labelEn: "Everyday life in Spain",
    group: "none",
  },
  {
    sv: "/kunskapsbank/trygghet-och-viktiga-nummer",
    en: "/en/knowledge-hub/safety-and-key-numbers",
    labelSv: "Trygghet och viktiga nummer",
    labelEn: "Safety and key numbers",
    group: "none",
  },
  {
    sv: "/svensk-forskola-i-spanien",
    en: "/en/swedish-preschool-in-spain",
    labelSv: "Svensk förskola i Spanien",
    labelEn: "Swedish preschool in Spain",
    group: "guides",
  },
  {
    sv: "/bo-i-cancelada-och-estepona",
    en: "/en/living-in-cancelada-and-estepona",
    labelSv: "Bo i Cancelada och Estepona",
    labelEn: "Living in Cancelada and Estepona",
    group: "guides",
  },
  {
    sv: "/valja-skola-utomlands",
    en: "/en/choosing-a-school-abroad",
    labelSv: "Välja skola utomlands",
    labelEn: "Choosing a school abroad",
    group: "guides",
  },

  {
    sv: "/integritetspolicy",
    en: "/en/privacy-policy",
    labelSv: "Integritetspolicy",
    labelEn: "Privacy policy",
    group: "legal",
  },
  {
    sv: "/cookiepolicy",
    en: "/en/cookie-policy",
    labelSv: "Cookiepolicy",
    labelEn: "Cookie policy",
    group: "legal",
  },
  {
    sv: "/anvandarvillkor",
    en: "/en/terms",
    labelSv: "Användarvillkor",
    labelEn: "Terms of use",
    group: "legal",
  },
  {
    sv: "/uppforandekod",
    en: "/code-of-conduct",
    labelSv: "Uppförandekod",
    labelEn: "Code of Conduct",
    group: "legal",
  },
];

export const SPANISH_PATHS: Readonly<Record<string, string>> = {
  "/": "/es",
  "/om-skolan": "/es/sobre-la-escuela",
  "/forskola": "/es/preescolar",
  "/forskoleklass": "/es/clase-preescolar",
  "/grundskola": "/es/primaria",
  "/hogstadium-gymnasium": "/es/secundaria-y-bachillerato",
  "/fritidsverksamhet": "/es/actividades-extraescolares",
  "/personal": "/es/equipo",
  "/omdomen": "/es/opiniones",
  "/elevhalsoteamet": "/es/bienestar-del-alumnado",
  "/extra-stod": "/es/apoyo-educativo",
  "/arskurser": "/es/cursos-escolares",
  "/fotbollsakademi": "/es/academia-de-futbol",
  "/padel-tennis": "/es/padel-tenis",
  "/hostlovscamp-mff-2026": "/es/campus-de-futbol-mff-2026",
  "/spanskakurs": "/es/aprender-espanol",
  "/avgifter": "/es/tarifas",
  "/lasarstider": "/es/calendario-escolar",
  "/matsedel": "/es/menu-escolar",
  "/blogg": "/es/blog",
  "/prenumerera": "/es/suscribirse",
  "/flyttgenomgang": "/es/asesoria-de-reubicacion",
  "/fragor-och-svar": "/es/preguntas-frecuentes",
  "/svenska-skolverket-och-kompletterande-svensk-undervisning": "/es/agencia-nacional-sueca-de-educacion",
  "/junta-de-andalucia-och-utbildning": "/es/junta-de-andalucia-y-educacion",
  "/instagram": "/es/instagram",
  "/sponsorer": "/es/patrocinadores",
  "/lediga-tjanster": "/es/empleo",
  "/boka-besok": "/es/reservar-visita",
  "/kontakt": "/es/contacto",
  "/ansokan": "/es/solicitud",
  "/gemenskap": "/es/comunidad",
  "/flytta-till-spanien-med-barn": "/es/mudarse-a-espana-con-ninos",
  "/svensk-skola-i-spanien": "/es/colegio-sueco-en-espana",
  "/skola-i-marbella-och-estepona": "/es/colegio-en-marbella-y-estepona",
  "/svensk-skola-marbella": "/es/colegio-sueco-marbella",
  "/skola-estepona": "/es/colegio-estepona",
  "/skola-cancelada": "/es/colegio-cancelada",
  "/nie-och-folkbokforing-i-spanien": "/es/nie-y-empadronamiento",
  "/distansstudier-hogstadiet-gymnasiet-utomlands": "/es/educacion-a-distancia-en-el-extranjero",
  "/guider": "/es/guias",
  "/kunskapsbank": "/es/centro-de-recursos",
  "/kunskapsbank/flyttchecklistan": "/es/centro-de-recursos/lista-para-la-mudanza",
  "/kunskapsbank/myndigheter-och-papper": "/es/centro-de-recursos/tramites-y-autoridades",
  "/kunskapsbank/vardagen-i-spanien": "/es/centro-de-recursos/vida-cotidiana-en-espana",
  "/kunskapsbank/trygghet-och-viktiga-nummer": "/es/centro-de-recursos/seguridad-y-telefonos-importantes",
  "/svensk-forskola-i-spanien": "/es/preescolar-sueco-en-espana",
  "/bo-i-cancelada-och-estepona": "/es/vivir-en-cancelada-y-estepona",
  "/valja-skola-utomlands": "/es/elegir-colegio-en-el-extranjero",
  "/svensk-internationell-eller-spansk-skola": "/es/colegio-sueco-internacional-o-espanol",
  "/vad-kostar-svensk-skola-i-spanien": "/es/cuanto-cuesta-la-escuela-sueca-en-espana",
  "/integritetspolicy": "/es/politica-de-privacidad",
  "/cookiepolicy": "/es/politica-de-cookies",
  "/anvandarvillkor": "/es/condiciones-de-uso",
  "/uppforandekod": "/es/codigo-de-conducta",
};

const SPANISH_LABELS: Record<string, string> = {
  "/": "Inicio", "/om-skolan": "La escuela", "/forskola": "Preescolar", "/forskoleklass": "Clase preescolar",
  "/grundskola": "Primaria",
  "/hogstadium-gymnasium": "Secundaria y bachillerato", "/fritidsverksamhet": "Actividades extraescolares",
  "/omdomen": "Opiniones de familias", "/personal": "Nuestro equipo", "/elevhalsoteamet": "Bienestar del alumnado", "/extra-stod": "Apoyo educativo",
  "/arskurser": "Cursos escolares", "/fotbollsakademi": "Academia de fútbol Malmö FF", "/padel-tennis": "Pádel y tenis",
  "/hostlovscamp-mff-2026": "Campus de fútbol", "/spanskakurs": "Aprender español", "/avgifter": "Tarifas",
  "/lasarstider": "Calendario escolar", "/matsedel": "Menú escolar", "/blogg": "Blog", "/prenumerera": "Suscribirse",
  "/flyttgenomgang": "Asesoría de reubicación", "/fragor-och-svar": "Preguntas frecuentes",
  "/svenska-skolverket-och-kompletterande-svensk-undervisning": "Agencia Nacional Sueca de Educación",
  "/junta-de-andalucia-och-utbildning": "Junta de Andalucía", "/instagram": "Instagram", "/sponsorer": "Patrocinadores",
  "/lediga-tjanster": "Empleo", "/boka-besok": "Reservar una visita", "/kontakt": "Contacto", "/ansokan": "Solicitud", "/gemenskap": "Comunidad",
  "/flytta-till-spanien-med-barn": "Mudarse a España con niños", "/svensk-skola-i-spanien": "Colegio sueco en España",
  "/skola-i-marbella-och-estepona": "Colegios en Marbella y Estepona", "/nie-och-folkbokforing-i-spanien": "NIE y empadronamiento",
  "/distansstudier-hogstadiet-gymnasiet-utomlands": "Educación a distancia", "/guider": "Todas las guías",
  "/kunskapsbank": "Centro de recursos", "/svensk-forskola-i-spanien": "Preescolar sueco en España",
  "/bo-i-cancelada-och-estepona": "Vivir en Cancelada y Estepona", "/valja-skola-utomlands": "Elegir colegio en el extranjero",
  "/svensk-internationell-eller-spansk-skola": "¿Colegio sueco, internacional o español?",
  "/vad-kostar-svensk-skola-i-spanien": "¿Cuánto cuesta la escuela sueca en España?",
  "/integritetspolicy": "Política de privacidad", "/cookiepolicy": "Política de cookies", "/anvandarvillkor": "Condiciones de uso", "/uppforandekod": "Código de conducta",
};

/** Svenska sidnycklar som har en riktig spansk route, inte catch-allens platshållare. */
const COMPLETE_SPANISH_PAGES = new Set([
  "/", "/avgifter", "/lasarstider", "/matsedel", "/spanskakurs", "/fragor-och-svar",
  "/kontakt", "/ansokan", "/blogg", "/prenumerera", "/integritetspolicy", "/cookiepolicy", "/anvandarvillkor", "/uppforandekod",
  "/kunskapsbank", "/lediga-tjanster", "/boka-besok", "/om-skolan",
  "/svensk-internationell-eller-spansk-skola", "/omdomen",
  "/forskola", "/forskoleklass", "/grundskola", "/personal", "/arskurser", "/padel-tennis",
  "/gemenskap", "/instagram", "/sponsorer", "/spanskakurs",
  "/junta-de-andalucia-och-utbildning",
  "/vad-kostar-svensk-skola-i-spanien",
]);

export function hasCompleteSpanishPage(svPath: string): boolean {
  return COMPLETE_SPANISH_PAGES.has(svPath);
}

function localizedPath(page: PageEntry, locale: Locale): string {
  if (locale === "en") return page.en;
  if (locale === "es") return SPANISH_PATHS[page.sv] ?? `/es${page.sv === "/" ? "" : page.sv}`;
  return page.sv;
}

function localizedLabel(page: PageEntry, locale: Locale): string {
  if (locale === "en") return page.labelEn;
  if (locale === "es") return SPANISH_LABELS[page.sv] ?? page.labelEn;
  return page.labelSv;
}

/** Lokaliserad navigationsetikett för en sida, med svensk sökväg som nyckel. */
export function labelFor(svPath: string, locale: Locale): string {
  const entry = PAGES.find((page) => page.sv === svPath);
  if (!entry) return svPath;
  return localizedLabel(entry, locale);
}

export function localeFromPathname(pathname: string): Locale {
  if (pathname === "/code-of-conduct") return "en";
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  if (pathname === "/es" || pathname.startsWith("/es/")) return "es";
  return "sv";
}

export function useLocale(): Locale {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return localeFromPathname(pathname);
}

/** Ger sökvägen för en sida i valt språk. Ta emot svensk sökväg som nyckel. */
export function pathFor(svPath: string, locale: Locale): string {
  const entry = PAGES.find((p) => p.sv === svPath);
  if (!entry) return locale === "sv" ? svPath : `/${locale}${svPath === "/" ? "" : svPath}`;
  return localizedPath(entry, locale);
}

/** Svenska sluggar under engelskt prefix som alltid ska gå till den riktiga engelska sidan. */
export const ENGLISH_SWEDISH_SLUG_REDIRECTS = Object.fromEntries(
  PAGES.flatMap((page) => {
    if (page.sv === "/") return [];
    const alias = `/en${page.sv}`;
    return alias === page.en ? [] : [[alias, page.en] as const];
  }),
) as Readonly<Record<string, string>>;

export function entryForPath(pathname: string): PageEntry | undefined {
  const clean = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
  return PAGES.find((p) => p.sv === clean || p.en === clean || localizedPath(p, "es") === clean);
}

export function pathsForAllLocales(pathname: string): Record<Locale, string> {
  const entry = entryForPath(pathname);
  if (!entry) return { sv: "/", en: "/en", es: "/es" };
  return { sv: entry.sv, en: entry.en, es: localizedPath(entry, "es") };
}

export function completeLocalesForPath(pathname: string): Partial<Record<Locale, string>> {
  const entry = entryForPath(pathname);
  if (!entry) return {};
  return {
    sv: entry.sv,
    en: entry.en,
    ...(hasCompleteSpanishPage(entry.sv) ? { es: localizedPath(entry, "es") } : {}),
  };
}

/** Motsvarande sida i det andra språket, behålls för äldre anrop. */
export function counterpartPath(pathname: string): string {
  const locale = localeFromPathname(pathname);
  const paths = pathsForAllLocales(pathname);
  return locale === "sv" ? paths.en : paths.sv;
}

export const NAV_GROUP_LABELS: Record<string, Record<Locale, string>> = {
  school: { sv: "Skolan", en: "The school", es: "El colegio" },
  sport: { sv: "Idrott & språk", en: "Sports & languages", es: "Deporte e idiomas" },
  practical: { sv: "Praktisk info", en: "Practical info", es: "Información práctica" },
  guides: { sv: "Guider", en: "Guides", es: "Guías" },
};

export function navGroupsFor(locale: Locale) {
  return (["school", "sport", "practical", "guides"] as const).map((group) => ({
    key: group,
    label: NAV_GROUP_LABELS[group]![locale],
    items: PAGES.filter((p) => p.group === group).map((p) => ({
      to: localizedPath(p, locale),
      label: localizedLabel(p, locale),
      emphasize: p.emphasize ?? false,
    })),
  }));
}

export function guideLinksFor(locale: Locale) {
  return PAGES.filter((p) => p.group === "guides").map((p) => ({
    to: localizedPath(p, locale),
    label: localizedLabel(p, locale),
  }));
}

export const UI_TEXT = {
  sv: {
    menu: "Huvudmeny",
    mobileMenu: "Mobilmeny",
    showMenu: "Visa meny",
    apply: "Ansökan",
    guides: "Guider",
    intro:
      "Svensk skola och förskola mellan Estepona och Marbella på Costa del Sol. Svensk läroplan, utökad spanska och engelska, små klasser.",
    switchTo: "English",
    switchLabel: "Byt till engelska",
  },
  en: {
    menu: "Main menu",
    mobileMenu: "Mobile menu",
    showMenu: "Show menu",
    apply: "Apply",
    guides: "Guides",
    intro:
      "Swedish school and preschool between Estepona and Marbella on the Costa del Sol. Swedish curriculum, extended Spanish and English, small classes.",
    switchTo: "Svenska",
    switchLabel: "Switch to Swedish",
  },
  es: {
    menu: "Menú principal",
    mobileMenu: "Menú móvil",
    showMenu: "Mostrar menú",
    apply: "Solicitud",
    guides: "Guías",
    intro:
      "Colegio y preescolar sueco entre Estepona y Marbella, en la Costa del Sol. Currículo sueco, español e inglés ampliados y grupos reducidos.",
    switchTo: "Svenska",
    switchLabel: "Cambiar idioma",
  },
} as const;
